import React, { useState, useEffect } from 'react';
import { Search, Lock, Pencil, Mic, Crown, Copy, Check, AlertCircle } from 'lucide-react';
import { promptsAPI } from '../services/api';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import PaywallModal from '../components/PaywallModal';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const iconMap = {
  pencil: Pencil,
  mic: Mic,
};

const tagColors = {
  'Premium': 'bg-purple-600 text-white',
  'Free': 'bg-green-600 text-white',
  'Recently Added': 'bg-green-600 text-white',
  'Productivity & Time-Saving': 'bg-blue-500 text-white',
  'Social Media': 'bg-teal-600 text-white',
  'Marketing & Advertising': 'bg-orange-500 text-white',
  'Analytics & Optimization': 'bg-emerald-600 text-white',
  'Universal': 'bg-gray-600 text-white',
  'Video': 'bg-red-500 text-white',
  'Branding': 'bg-pink-500 text-white',
  'Image': 'bg-indigo-500 text-white',
  'Content Creation': 'bg-cyan-500 text-white',
  'Analytics': 'bg-emerald-600 text-white',
};

const Prompts = () => {
  const [categories, setCategories] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { canAccessPrompt, isAuthenticated } = useAuth();

  useEffect(() => {
    fetchCategories();
    fetchPrompts();
  }, []);

  useEffect(() => {
    fetchPrompts();
  }, [selectedCategory, searchQuery]);

  const fetchCategories = async () => {
    try {
      const response = await promptsAPI.getCategories();
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      setCategories([]);
    }
  };

  const fetchPrompts = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = {};
      if (selectedCategory && selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }
      const response = await promptsAPI.getAll(params);
      setPrompts(response.data || []);
    } catch (error) {
      console.error('Failed to fetch prompts:', error);
      setError('Failed to load prompts. Please try again.');
      setPrompts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePromptClick = async (prompt) => {
    if (!prompt) return;
    
    if (canAccessPrompt(prompt)) {
      try {
        const response = await promptsAPI.getById(prompt.id);
        setSelectedPrompt(response.data);
        setDetailModalOpen(true);
      } catch (error) {
        console.error('Failed to fetch prompt details:', error);
        setError('Failed to load prompt details.');
      }
    } else {
      setSelectedPrompt(prompt);
      setPaywallOpen(true);
    }
  };

  const handleCopyPrompt = async (prompt, e) => {
    e?.stopPropagation();
    if (!prompt) return;
    
    if (!canAccessPrompt(prompt)) {
      setSelectedPrompt(prompt);
      setPaywallOpen(true);
      return;
    }
    
    try {
      const response = await promptsAPI.getById(prompt.id);
      const content = response.data?.prompt_content || `${prompt.title}\n\n${prompt.description}`;
      await navigator.clipboard.writeText(content);
      setCopiedId(prompt.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error('Failed to copy prompt:', error);
    }
  };

  // Loading state
  if (isLoading && prompts.length === 0) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-white">Browse Prompts</h1>
        <div className="flex justify-center py-12">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Loading prompts...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && prompts.length === 0) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-white">Browse Prompts</h1>
        <div className="flex justify-center py-12">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-400 mb-4">{error}</p>
            <Button onClick={fetchPrompts} className="bg-purple-600 hover:bg-purple-700">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-white">Browse Prompts</h1>

      <div className="flex gap-6">
        {/* Categories Sidebar */}
        <div className="w-[280px] flex-shrink-0">
          <div className="bg-[#1a1a2e] rounded-xl p-4 border border-gray-800">
            <h2 className="text-white font-semibold mb-4">Categories</h2>
            <nav className="space-y-1">
              {categories?.length > 0 ? (
                categories.map((category) => (
                  <button
                    key={category?.id || category?.slug || Math.random()}
                    onClick={() => setSelectedCategory(category?.slug || 'all')}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors text-sm ${
                      selectedCategory === category?.slug
                        ? 'bg-purple-600/30 text-purple-400'
                        : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
                    }`}
                  >
                    {category?.name || 'Unknown'}
                    {category?.count != null && (
                      <span className="ml-1 text-gray-500">({category.count})</span>
                    )}
                  </button>
                ))
              ) : (
                <p className="text-gray-500 text-sm">No categories available</p>
              )}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
              <Input
                type="text"
                placeholder="Search prompts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#1a1a2e] border-gray-700 text-white placeholder:text-gray-500 focus:border-purple-500"
              />
            </div>
          </div>

          {/* Loading State */}
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Prompts Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {prompts?.length > 0 ? (
                  prompts.map((prompt) => {
                    if (!prompt) return null;
                    const isLocked = !canAccessPrompt(prompt);

                    return (
                      <div
                        key={prompt.id || Math.random()}
                        onClick={() => handlePromptClick(prompt)}
                        className={`bg-[#1a1a2e] rounded-xl p-4 border transition-all cursor-pointer group ${
                          isLocked 
                            ? 'border-gray-800 hover:border-yellow-500/50' 
                            : 'border-gray-800 hover:border-purple-500/50'
                        }`}
                      >
                        <div className="flex gap-4">
                          {/* Thumbnail/Icon */}
                          <div className="flex-shrink-0">
                            {prompt.image_url ? (
                              <img
                                src={prompt.image_url}
                                alt={prompt.title || 'Prompt'}
                                className="w-16 h-16 rounded-lg object-cover"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = '';
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="w-16 h-16 rounded-lg bg-purple-600 flex items-center justify-center">
                                <Pencil size={24} className="text-white" />
                              </div>
                            )}
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            {/* Tags */}
                            <div className="flex flex-wrap gap-1 mb-2">
                              {prompt.tags?.slice(0, 4).map((tag, index) => (
                                <Badge
                                  key={`${prompt.id}-tag-${index}`}
                                  className={`text-xs px-2 py-0.5 ${tagColors[tag] || 'bg-gray-600 text-white'}`}
                                >
                                  {tag === 'Premium' && <Crown size={10} className="mr-1 inline" />}
                                  {tag}
                                </Badge>
                              ))}
                            </div>

                            {/* Title */}
                            <h3 className="text-white font-semibold mb-2 group-hover:text-purple-400 transition-colors">
                              {prompt.title || 'Untitled Prompt'}
                            </h3>

                            {/* Description */}
                            {prompt.description && (
                              <p className="text-gray-400 text-sm line-clamp-2">
                                {prompt.description}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Footer with Lock/Copy */}
                        <div className="flex justify-between items-center mt-3">
                          {isLocked ? (
                            <div className="flex items-center gap-2 text-yellow-500 text-sm">
                              <Lock size={14} />
                              <span>Upgrade to unlock</span>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => handleCopyPrompt(prompt, e)}
                              className="text-gray-400 hover:text-white h-8"
                            >
                              {copiedId === prompt.id ? (
                                <><Check size={14} className="mr-1 text-green-500" /> Copied!</>
                              ) : (
                                <><Copy size={14} className="mr-1" /> Copy Prompt</>
                              )}
                            </Button>
                          )}
                          
                          {isLocked && (
                            <div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center">
                              <Lock size={16} className="text-yellow-500" />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : null}
              </div>

              {(!prompts || prompts.length === 0) && (
                <div className="text-center py-12">
                  <p className="text-gray-400">No prompts found matching your criteria.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Paywall Modal */}
      <PaywallModal
        isOpen={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        promptTitle={selectedPrompt?.title}
      />

      {/* Prompt Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="bg-[#1a1a2e] border-gray-800 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">{selectedPrompt?.title || 'Prompt Details'}</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <div className="flex flex-wrap gap-2 mb-4">
              {selectedPrompt?.tags?.map((tag, index) => (
                <Badge key={index} className={`${tagColors[tag] || 'bg-gray-600'}`}>
                  {tag}
                </Badge>
              ))}
            </div>
            
            <p className="text-gray-400 mb-6">{selectedPrompt?.description}</p>
            
            <div className="bg-[#12121a] rounded-lg p-4 border border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Prompt Content</span>
                <Button
                  size="sm"
                  onClick={() => {
                    if (selectedPrompt?.prompt_content) {
                      navigator.clipboard.writeText(selectedPrompt.prompt_content);
                      setCopiedId(selectedPrompt?.id);
                      setTimeout(() => setCopiedId(null), 2000);
                    }
                  }}
                  className="bg-purple-600 hover:bg-purple-700 h-8"
                >
                  {copiedId === selectedPrompt?.id ? (
                    <><Check size={14} className="mr-1" /> Copied!</>
                  ) : (
                    <><Copy size={14} className="mr-1" /> Copy</>
                  )}
                </Button>
              </div>
              <pre className="text-white text-sm whitespace-pre-wrap font-mono">
                {selectedPrompt?.prompt_content || 'No content available'}
              </pre>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Prompts;
